import type { FC, ReactNode } from 'react'
import type { CitySlug, ServiceSlug } from '@/types'

import { Mail, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AssetTag } from '@/components/generals/asset-tag'
import { EnquiryForm } from '@/components/generals/enquiry-form'
import { siteConfig } from '@/constants/site'

interface EnquirySectionProps {
    title?: string
    intro?: string
    headingLevel?: 'h1' | 'h2'
    defaultService?: ServiceSlug
    defaultCity?: CitySlug
    className?: string
}

const NEXT_STEPS = [
    {
        title: "We call you back",
        text: "A short call to understand your requirement, location and timeline."
    },
    {
        title: "You get a clear quote",
        text: "Configurations, monthly rent or price, terms and a delivery date in writing."
    },
    {
        title: "We deliver and support",
        text: "Devices arrive configured and tested, and we stay on call for the whole term."
    }
]

export const EnquirySection: FC<EnquirySectionProps> = ({
    title = "Tell us what your team needs",
    intro = "Share a few details and we will call you back with the right configuration and a clear quote. It takes about a minute and there is no obligation.",
    headingLevel = 'h2',
    defaultService,
    defaultCity,
    className
}): ReactNode => {
    const Heading = headingLevel

    return (
        <section
            id='enquiry'
            aria-labelledby='enquiry-title'
            className={cn('bg-secondary py-20 md:py-28', className)}
        >
            <div className='shell grid gap-12 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <AssetTag>Free quote</AssetTag>

                    <Heading id='enquiry-title' className='mt-5 text-headline font-semibold'>
                        {title}
                    </Heading>

                    <p className='mt-5 text-lg leading-relaxed text-muted-foreground'>
                        {intro}
                    </p>

                    <ol className='mt-10 space-y-6'>
                        {NEXT_STEPS.map((step, index) => (
                            <li key={step.title} className='flex gap-4'>
                                <span className='w-7 shrink-0 pt-0.5 font-mono text-sm text-muted-foreground'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <div>
                                    <p className='font-semibold'>
                                        {step.title}
                                    </p>

                                    <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                        {step.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className='mt-10 space-y-3 border-t border-dashed border-foreground/25 pt-8'>
                        <a
                            href={siteConfig.phone.href}
                            className='group flex w-fit items-center gap-3 rounded-md text-lg font-semibold'
                        >
                            <span className='flex size-10 items-center justify-center rounded-full bg-ink text-ink-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground'>
                                <Phone aria-hidden='true' className='size-4' />
                            </span>
                            {siteConfig.phone.display}
                        </a>

                        <a
                            href={`mailto:${siteConfig.email}`}
                            className='group flex w-fit items-center gap-3 rounded-md text-lg font-semibold'
                        >
                            <span className='flex size-10 items-center justify-center rounded-full border border-foreground/20 transition-colors group-hover:border-foreground'>
                                <Mail aria-hidden='true' className='size-4' />
                            </span>
                            {siteConfig.email}
                        </a>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <div className='rounded-2xl bg-card p-6 shadow-[0_30px_60px_-40px_rgb(18_21_28/0.45)] ring-1 ring-foreground/10 sm:p-8 md:p-10'>
                        <EnquiryForm defaultService={defaultService} defaultCity={defaultCity} />
                    </div>
                </div>
            </div>
        </section>
    )
}
